import { useEffect, useState } from 'react';
import { get, post } from '../../../Services/http';
import { DAYS_PER_REQUEST, FROM_DATE, TO_DATE } from './constants';
import { buildDateWindows } from './salesCalculations';

// The http service throws for non-2xx responses and puts the response on the error
export const toError = async (err) => {
    if (!err.response) return err;

    const body = await err.response.json().catch(() => null);
    return new Error(body?.error ? `HTTP ${err.response.status}: ${body.error}` : `HTTP ${err.response.status}`);
};

export const fetchJson = async (url) => {
    try {
        const res = await get(url);
        return await res.json();
    } catch (err) {
        throw await toError(err);
    }
};

// Resolves to null when the server answers 204 No Content
const fetchOptionalJson = async (url) => {
    try {
        const res = await get(url);
        return res.status === 204 ? null : await res.json();
    } catch (err) {
        throw await toError(err);
    }
};

// Loads all invoices of the sales data period, from the cached full dataset when it exists
export const useSalesInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    // progress is null while invoice heads are being fetched, then { loaded, total } while details load
    const [progress, setProgress] = useState(null);
    // Invoice heads for which no invoice details could be fetched, as { documentNumber, error }
    const [missingInvoices, setMissingInvoices] = useState([]);
    const [fetchError, setFetchError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const cached = await fetchOptionalJson(`/api/sales-data/full?fromDate=${FROM_DATE}&toDate=${TO_DATE}`);
                if (cancelled) return;

                if (cached) {
                    setInvoices(cached.invoices || []);
                    setLoading(false);
                    return;
                }

                const heads = await fetchJson(`/api/sales-data/heads?fromDate=${FROM_DATE}&toDate=${TO_DATE}`);
                if (cancelled) return;

                const headNumbers = heads.map((head) => head.documentNumber).filter(Boolean);
                const loadedInvoices = [];
                setProgress({ loaded: 0, total: headNumbers.length });

                // Skip date windows where the invoice heads show there are no invoices
                const headDates = heads.map((head) => (head.invoiceDate || '').substring(0, 10)).filter(Boolean);
                const windows = buildDateWindows(FROM_DATE, TO_DATE, DAYS_PER_REQUEST)
                    .map((w) => ({ ...w, count: headDates.filter((date) => date >= w.from && date <= w.to).length }))
                    .filter((w) => w.count > 0);

                for (const range of windows) {
                    setProgress({ loaded: Math.min(loadedInvoices.length, headNumbers.length), total: headNumbers.length, range });

                    const data = await fetchJson(`/api/sales-data?fromDate=${range.from}&toDate=${range.to}`);
                    if (cancelled) return;

                    loadedInvoices.push(...(data.invoices || []));
                }

                setProgress({ loaded: Math.min(loadedInvoices.length, headNumbers.length), total: headNumbers.length });

                const loadedNumbers = new Set(loadedInvoices.map((invoice) => invoice.documentNumber));
                const missing = [];
                for (const documentNumber of headNumbers.filter((n) => !loadedNumbers.has(n))) {
                    try {
                        loadedInvoices.push(await fetchJson(`/api/sales-data/invoices/${documentNumber}`));
                    } catch (err) {
                        missing.push({ documentNumber, error: err.message });
                    }
                    if (cancelled) return;
                }

                setMissingInvoices(missing);
                setInvoices(loadedInvoices);
                setLoading(false);

                // Lets the next visit load the full dataset from the cache; skipped when the dataset is incomplete
                if (missing.length === 0) {
                    post(`/api/sales-data/full?fromDate=${FROM_DATE}&toDate=${TO_DATE}`).catch(() => { });
                }
            } catch (err) {
                if (cancelled) return;
                setFetchError(err.message);
                setLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, []);

    return { invoices, setInvoices, loading, progress, missingInvoices, fetchError };
};
